import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
// @ts-ignore
import { useNavigate } from "react-router-dom";
import { gql, useApolloClient, useReactiveVar, useMutation } from '@apollo/client';
import SvgEdit24Px from '../icons/SvgEdit';
import SvgAddPlus24Px from '../icons/SvgPlus';
import BenefitList from './BenefitList';
import Button, { ButtonVariant } from './Button';
import LoadingScreen from './LoadingScreen';
import ErrorScreen from './ErrorScreen';
import TextInput, { TextInputVariants } from './TextInput';
import SvgX from '../icons/SvgX';
import { saveMemberships, writeMembershipTier } from "../cache/transformers";
import ContentEditable from "react-contenteditable";
import { isSubscriptionBenefit } from "./BenefitList";
import { useDeleteMembershipTier } from "../cache/MembershipLevelFunctions";
import { currencyCodeVar, membershipsHaveBeenEditedVar, currentNewlyCreatedDefaultProductUUIDsVar, currentShopifyVersionVar, currentWriteInBenefitVar, primaryActionLoadingVar, dummyFunc, pageHasUnsavedChangesVar, startPrimaryAction, completePrimaryAction, currentBusinessIdVar, setUnsavedTierEdits, clearUnsavedTierEdits, unsavedMembershipTierEditsVar } from "../helpers/client";
import { majorToMinorPrice, minorToMajorPrice, stripHtml, preventEnter, handleNumberFieldKeyDown, getPrecision, decodeSanitizedText } from "../helpers/tools";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { BsQuestionCircleFill } from "react-icons/bs";
import { IconContext } from "react-icons";
import { debounce } from 'lodash';
import { useMembershipLevelComponentQuery, useCreateBlankSimpleBenefitMutation, useCreateBlankSubscriptionBenefitMutation, useCreateBlankWelcomeGiftMutation, useUpdateMembershipMutation, DeliveryStrategy, Period, CurationMatchMode, CurationStatus, } from '../types';
import MembershipLevelImageUpload from "./MembershipLevelImageUpload";
import { randomRegularBenefit } from '../helpers/randomBenefits';
import ModalConfirmation from './ModalConfirmation';
import { v4 as uuid } from 'uuid';
import { useI18n } from '@shopify/react-i18n';
import { helpText } from '../helpers/helpText';
gql `
  query MembershipLevelComponent($id: ID!) {
    membershipTier(id: $id) {
      id
      name
      manualDiscountCode
      defaultMode
      overridePlatformProductSync
      isSubscription
      members {
        id
      }
      business {
        id
        status
        membershipTiers(kind: Live) {
          id
          name
          isSubscription
          plans {
            kind
            price { amount }
          }
          benefits {
            id
          }
          subscriptionBenefits {
            id
            delivery
            deliveryFrequency
            shippingFrequency {
              number
              period
            }
            firstShipmentDelay
            shippingIncluded
            nextShipmentDate
            curationPreferOverstock
            curationMatchMode
          }
        }
      }
      plans {
        kind
        price {
          amount
        }
      }
      discounts {
        scope
        percentage
        guests
      }
      earlyAccess
      birthdayCouponAmount
      birthdayCouponKind
      image {
        id
        url
        cdnPath
      }
      welcomeGift {
        __typename
        id
        name
        shippingNotesInstructions
        membershipTiers {
          id
          name
        }
        products {
          ...SubscriptionBenefitProduct
        }
        variants {
          ...VariantPanel
        }
        delivery
        shippingEnabled
        pickupEnabled
        shippingCost {
          amount
        }
        shopifyProductIds
      }
      subscriptionBenefits {
        id
        name
        membershipTiers {
          id
          name
          members(status: Active) {
            id
          }
          plan(kind: MONTHLY) {
            price {
              amount
            }
          }
        }
        delivery
        deliveryFrequency
        shippingFrequency {
          __typename
          number
          period
        }
        firstShipmentDelay
        nextShipmentDate
        isAddonOnly
        products {
          ...SubscriptionBenefitProduct
        }
        variants {
          ...VariantPanel
        }
        addons {
          ...AddonPanel
        }
        shopifyProductIds
        shippingIncluded
        shippingWeight
        shippingNotesInstructions
        curationPreferOverstock
        curationMatchMode
        shippingEnabled
        pickupEnabled
        shippingCost {
          amount
        }
      }
      benefits {
        __typename
        id
        name
        membershipTiers {
          id
          name
          members(status: Active) {
            id
          }
          plan(kind: MONTHLY) {
            price {
              amount
            }
          }
        }
        ... on RegularBenefit {
          order
        }
        ...on WelcomeGiftBenefit {
          delivery
          shopifyProductIds
        }
        ...on SubscriptionBenefit {
          delivery
          deliveryFrequency
          shippingFrequency {
            __typename
            number
            period
          }
          firstShipmentDelay
          nextShipmentDate
          shopifyProductIds
          shippingIncluded
          shippingWeight
          shippingNotesInstructions
          curationPreferOverstock
          curationMatchMode
          shippingEnabled
          pickupEnabled
          shippingCost {
            amount
          }

          isAddonOnly
          products {
            ...SubscriptionBenefitProduct
          }
          variants {
            ...VariantPanel
          }
          addons {
            ...AddonPanel
          }
        }
      }
    }
  }
`;
const DUPLICATE_TIER_SOURCE = gql `
  fragment DuplicateTierSource on MembershipTier {
    name
    defaultMode
    overridePlatformProductSync
    image { id }
    plans { kind price { amount } }
    discounts { scope percentage guests }
    earlyAccess
    birthdayCouponAmount
    birthdayCouponKind
    welcomeGift {
      __typename
    id
      name
      shippingNotesInstructions
      products {
        id
        name
        platformId
        platformName
        platformVariantsOnlyMode
        variants {
          id
          description
          additionalCost { amount }
          platformName
          platformId
        }
      }
      variants {
        id
        description
        additionalCost { amount }
        platformName
        platformId
      }
    }
    benefits {
      __typename
      id
      name
      ... on RegularBenefit { order }
      ... on SubscriptionBenefit {
        delivery
        nextShipmentDate
        isAddonOnly
        shippingFrequency { number period }
        deliveryFrequency
        firstShipmentDelay
        shippingIncluded
        shippingWeight
        shippingNotesInstructions
        curationMatchMode
        curationPreferOverstock
        products {
          id
          name
          platformId
          platformName
          platformVariantsOnlyMode
          curationValue
          curationCollections
          curationExcludeProducts
          variants {
            id
            description
            additionalCost { amount }
            platformName
            platformId
            curationExcludeProducts
            curationIncludeVariants
            curationSearchTerms
            curationStatus
          }
        }
        variants {
          id
          description
          additionalCost { amount }
          platformName
          platformId
        }
        addons {
          id
          description
          applyDiscount
          dontRollOver
          platformId
          platformName
          additionalCost { amount }
        }
      }
    }
  }
`;
export const PrimaryAction = "Membership Tiers";
export var MembershipLevelAction;
(function (MembershipLevelAction) {
    MembershipLevelAction["CreatedSubscriptionBenefit"] = "Created Subscription Benefit";
    MembershipLevelAction["EditedSubscriptionBenefit"] = "Edited Subscription Benefit";
    MembershipLevelAction["CreatedRegularBenefit"] = "Created Regular Benefit";
    MembershipLevelAction["EditedMembershipLevel"] = "Edited Membership Tier";
    MembershipLevelAction["DeletedMembershipLevel"] = "Deleted Membership Tier";
    MembershipLevelAction["UpdatedMembershipImage"] = "Updated Membership Image";
    MembershipLevelAction["CreatedWelcomeGift"] = "Created Welcome Gift";
})(MembershipLevelAction || (MembershipLevelAction = {}));
const MembershipLevel = ({ position, suppressDelete, id, extraButtons, collections, strategyPrice, strategyDiscount, useSyncShopifyDataMutation = dummyFunc, layoutVariant = 'multi' }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36;
    const client = useApolloClient();
    const [i18n] = useI18n();
    const navigate = useNavigate();
    const { symbol: currencySymbol } = i18n.getCurrencySymbol(currencyCodeVar());
    const currentShopifyVersion = currentShopifyVersionVar();
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    const membershipsHaveBeenEdited = useReactiveVar(membershipsHaveBeenEditedVar);
    const currentBusinessId = useReactiveVar(currentBusinessIdVar);
    const [showModal, setShowModal] = useState(false);
    const tierNameInputRef = useRef(null);
    const priceInputRef = useRef(null);
    const retailDiscountInputRef = useRef(null);
    const retailDiscountRef = useRef('');
    const eventDiscountInputRef = useRef(null);
    const eventDiscountRef = useRef('');
    const eventDiscountGuestsInputRef = useRef(null);
    const eventDiscountGuestsRef = useRef('');
    const birthdayCouponAmountInputRef = useRef(null);
    const birthdayCouponAmountRef = useRef('');
    const birthdayCouponKindRef = useRef('');
    const monthlyPriceRef = useRef('');
    const shippingWeightInputRef = useRef(null);
    const shippingWeightRef = useRef('');
    const [editedName, setEditedName] = useState(false);
    const { wrapped: deleteMembershipTier } = useDeleteMembershipTier(id);
    const { data, loading, error } = useMembershipLevelComponentQuery({
        variables: { id },
    });
    const [syncToShopify] = useSyncShopifyDataMutation({
        variables: {
            id: (_b = (_a = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _a === void 0 ? void 0 : _a.business) === null || _b === void 0 ? void 0 : _b.id
        },
        skip: !((_d = (_c = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _c === void 0 ? void 0 : _c.business) === null || _d === void 0 ? void 0 : _d.id),
    });
    const [loadingCreateSubscriptionBenefit, setLoadingCreateSubscriptionBenefit] = useState(false);
    const [loadingCreateWelcomeGift, setLoadingCreateWelcomeGift] = useState(false);
    const [loadingCreateBenefit, setLoadingCreateBenefit] = useState(false);
    const [loadingCreateRedemptionBenefit, setLoadingCreateRedemptionBenefit] = useState(false);
    const [updateMembership] = useUpdateMembershipMutation();
    // Local controlled inputs for unstable ContentEditable fields
    const [retailDiscountInput, setRetailDiscountInput] = useState('0');
    const [retailFocused, setRetailFocused] = useState(false);
    const [eventDiscountInput, setEventDiscountInput] = useState('0');
    const [eventFocused, setEventFocused] = useState(false);
    const [eventGuestsInput, setEventGuestsInput] = useState('0');
    const [guestsFocused, setGuestsFocused] = useState(false);
    const [birthdayAmountInput, setBirthdayAmountInput] = useState('0');
    const [birthdayFocused, setBirthdayFocused] = useState(false);
    // Find first tier with a subscription benefit
    const existingTier = (_g = (_f = (_e = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _e === void 0 ? void 0 : _e.business) === null || _f === void 0 ? void 0 : _f.membershipTiers) === null || _g === void 0 ? void 0 : _g.find(tier => { var _a; return ((_a = tier.subscriptionBenefits) === null || _a === void 0 ? void 0 : _a.length) > 0; });
    const existingBenefit = (_h = existingTier === null || existingTier === void 0 ? void 0 : existingTier.subscriptionBenefits) === null || _h === void 0 ? void 0 : _h[0];
    const existingCurationBenefit = (_k = (_j = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _j === void 0 ? void 0 : _j.subscriptionBenefits) === null || _k === void 0 ? void 0 : _k[0];
    // Default values if no existing benefit found
    const shippingIncluded = (_l = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.shippingIncluded) !== null && _l !== void 0 ? _l : true;
    const subscription = {
        shippingIncluded,
        name: `Curated item from our ${collections.length ? collections[Math.floor(Math.random() * collections.length)] + " " : ""}collection`,
        delivery: (_m = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.delivery) !== null && _m !== void 0 ? _m : DeliveryStrategy.Quarterly,
        curationMatchMode: (_o = existingCurationBenefit === null || existingCurationBenefit === void 0 ? void 0 : existingCurationBenefit.curationMatchMode) !== null && _o !== void 0 ? _o : CurationMatchMode.Product,
        curationPreferOverstock: (_p = existingCurationBenefit === null || existingCurationBenefit === void 0 ? void 0 : existingCurationBenefit.curationPreferOverstock) !== null && _p !== void 0 ? _p : false,
        deliveryFrequency: (_q = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.deliveryFrequency) !== null && _q !== void 0 ? _q : 3,
        shippingFrequency: (_r = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.shippingFrequency) !== null && _r !== void 0 ? _r : {
            number: 3,
            period: Period.Month
        },
        firstShipmentDelay: (_s = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.firstShipmentDelay) !== null && _s !== void 0 ? _s : 0,
        nextShipmentDate: (_t = existingBenefit === null || existingBenefit === void 0 ? void 0 : existingBenefit.nextShipmentDate) !== null && _t !== void 0 ? _t : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        products: [{
                id: uuid(),
                name: `Subscription Box Preference`,
                variants: [{
                        id: uuid(),
                        description: "Standard",
                        additionalCost: {
                            amount: 0,
                        },
                        curationStatus: CurationStatus.Approved,
                    }],
            }],
        shippingEnabled: true,
        pickupEnabled: true,
    };
    if (shippingIncluded) {
        subscription.shippingCost = { amount: 0 };
    }
    const subscriptionBenefitVariables = {
        membershipTierId: id,
        subscription,
    };
    const [createSubscriptionBenefit] = useCreateBlankSubscriptionBenefitMutation({
        ignoreResults: true,
        variables: subscriptionBenefitVariables,
        update: (cache, response) => {
            var _a;
            const addBenefit = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.addBenefit;
            cache.modify({
                id: `MembershipTier:${id}`,
                fields: {
                    subscriptionBenefit: (_, { toReference }) => {
                        return toReference(addBenefit);
                    },
                    benefits: (existingBenefitRefs = [], { readField }) => {
                        if (!existingBenefitRefs) {
                            existingBenefitRefs = [];
                        }
                        // KGAUDIT - is this a poor man's toReference?
                        const newBenefitRef = { __ref: `${addBenefit.__typename}:${addBenefit.id}` };
                        if (existingBenefitRefs.some(ref => readField('id', ref) === addBenefit.id)) {
                            return existingBenefitRefs;
                        }
                        return [...existingBenefitRefs, newBenefitRef];
                    }
                }
            });
            setLoadingCreateSubscriptionBenefit(false);
            setTimeout(() => {
                var _a;
                // Restore tier name after Apollo automatic cache update
                cache.modify({
                    id: `MembershipTier:${id}`,
                    fields: {
                        name: () => tierNameInputRef.current.value
                    },
                });
                // This is some typescript bullshit
                if ((addBenefit === null || addBenefit === void 0 ? void 0 : addBenefit.__typename) === "SubscriptionBenefit")
                    currentNewlyCreatedDefaultProductUUIDsVar(currentNewlyCreatedDefaultProductUUIDsVar().concat((_a = addBenefit === null || addBenefit === void 0 ? void 0 : addBenefit.products[0]) === null || _a === void 0 ? void 0 : _a.id));
                navigate(`/memberships/${id}/subscription/${addBenefit.id}`);
            }, 500);
            // mixpanel.track(PrimaryAction, { action: MembershipLevelAction.CreatedSubscriptionBenefit })
        },
    });
    // Redemption Benefit defaults
    const redemption = {
        name: "1 free ARC (advanced reader copy) book per month",
        delivery: DeliveryStrategy.Monthly,
        perPeriod: 1,
        shippingNotesInstructions: "Which book did they get?",
    };
    const CREATE_REDEMPTION = gql `
    mutation CreateBlankRedemptionBenefitInline($membershipTierId: ID!, $redemption: RedemptionBenefitInput!) {
      addBenefit(membershipTierId: $membershipTierId, benefit: { redemption: $redemption }) {
        __typename
        id
      }
    }
  `;
    const [mutateCreateRedemption] = useMutation(CREATE_REDEMPTION);
    const createRedemptionBenefit = async () => {
        var _a;
        const response = await mutateCreateRedemption({ variables: { membershipTierId: id, redemption } });
        const addBenefit = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.addBenefit;
        if (!addBenefit)
            return;
        client.cache.modify({
            id: `MembershipTier:${id}`,
            fields: {
                benefits: (existingBenefitRefs = [], { readField }) => {
                    const newRef = { __ref: `${addBenefit.__typename}:${addBenefit.id}` };
                    if (existingBenefitRefs.some(ref => readField('id', ref) === addBenefit.id)) {
                        return existingBenefitRefs;
                    }
                    return [...existingBenefitRefs, newRef];
                },
            }
        });
        setLoadingCreateRedemptionBenefit(false);
        setTimeout(() => {
            client.cache.modify({ id: `MembershipTier:${id}`, fields: { name: () => tierNameInputRef.current.value } });
            navigate(`/memberships/${id}/redemption/${addBenefit.id}`);
        }, 300);
    };
    const [createWelcomeGift] = useCreateBlankWelcomeGiftMutation({
        ignoreResults: true,
        variables: {
            membershipTierId: id,
            name: `Welcome Gift`,
            productId: uuid(),
            variantId: uuid(),
            productName: `Welcome Gift Preference`,
        },
        update: (cache, response) => {
            var _a;
            const addBenefit = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.addBenefit;
            cache.modify({
                id: `MembershipTier:${id}`,
                fields: {
                    welcomeGift: () => { return { __ref: `${addBenefit.__typename}:${addBenefit.id}` }; },
                }
            });
            setLoadingCreateWelcomeGift(false);
            setTimeout(() => {
                var _a;
                // Restore tier name after Apollo automatic cache update
                cache.modify({
                    id: `MembershipTier:${id}`,
                    fields: {
                        name: () => tierNameInputRef.current.value
                    },
                });
                // @ts-ignore
                currentNewlyCreatedDefaultProductUUIDsVar(currentNewlyCreatedDefaultProductUUIDsVar().concat((_a = addBenefit === null || addBenefit === void 0 ? void 0 : addBenefit.products[0]) === null || _a === void 0 ? void 0 : _a.id));
                navigate(`/memberships/${id}/welcome/${addBenefit.id}`);
            }, 500);
            // mixpanel.track(PrimaryAction, { action: MembershipLevelAction.CreatedWelcomeGift })
        },
    });
    const [createBenefit] = useCreateBlankSimpleBenefitMutation({
        // KGAUDIT - Why are we ignoring results? Because we're not using them? 
        ignoreResults: true,
        variables: { membershipTierId: id, name: randomRegularBenefit() },
        update: (cache, response) => {
            var _a;
            const addBenefit = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.addBenefit;
            currentWriteInBenefitVar(addBenefit === null || addBenefit === void 0 ? void 0 : addBenefit.id);
            cache.modify({
                id: `MembershipTier:${id}`,
                fields: {
                    benefits(existingBenefitRefs = [], { readField }) {
                        if (!existingBenefitRefs) {
                            existingBenefitRefs = [];
                        }
                        const newBenefitRef = {
                            __ref: `${addBenefit.__typename}:${addBenefit.id}`,
                        };
                        if (existingBenefitRefs.some((ref) => readField("id", ref) === addBenefit.id)) {
                            return existingBenefitRefs;
                        }
                        return [...existingBenefitRefs, newBenefitRef];
                    },
                },
            });
            setLoadingCreateBenefit(false);
            // Restore tier name after Apollo automatic cache update
            setTimeout(() => cache.modify({
                id: `MembershipTier:${id}`,
                fields: { name: () => tierNameInputRef.current.value },
            }), 1000);
            // mixpanel.track(PrimaryAction, { action: MembershipLevelAction.CreatedRegularBenefit })
        },
    });
    const updateTierName = (id) => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            // mixpanel.track(PrimaryAction, { 
            //   action: MembershipLevelAction.EditedMembershipLevel,
            //   field: "name",
            // })
            client.writeFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment UpdateTierName on MembershipTier {
            name
          }
        `,
                data: {
                    name: stripHtml(ev.target.value)
                }
            });
            setUnsavedTierEdits(tier.id, { name: stripHtml(ev.target.value) });
        };
    };
    const updateTierPrice = (id) => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            membershipsHaveBeenEditedVar(true);
            // mixpanel.track(PrimaryAction, { 
            //   action: MembershipLevelAction.EditedMembershipLevel,
            //   field: "price",
            // })
            const currencyValue = parseFloat(stripHtml(ev.target.value)) > 0 ? parseFloat(stripHtml(ev.target.value)) : 1;
            const newPrice = majorToMinorPrice(currencyValue);
            client.writeFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment NewMembership on MembershipTier {
            defaultMode
            plans {
              kind
              price {
                amount
              }
            }
          }
        `,
                data: {
                    defaultMode: false,
                    plans: [
                        {
                            kind: "MONTHLY",
                            price: {
                                amount: newPrice,
                            },
                        },
                        {
                            kind: "ANNUAL",
                            price: {
                                amount: newPrice * 12,
                            },
                        },
                    ]
                }
            });
            setUnsavedTierEdits(tier.id, {
                plans: [
                    { kind: 'MONTHLY', price: { amount: newPrice } },
                    { kind: 'ANNUAL', price: { amount: newPrice * 12 } },
                ],
            });
        };
    };
    const updateTierEventDiscountGuests = () => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            membershipsHaveBeenEditedVar(true);
            const newGuests = stripHtml(ev.target.value).match(/^\d+$/) ? parseInt(stripHtml(ev.target.value)) : 0;
            client.cache.modify({
                id: `MembershipTier:${tier.id}`,
                fields: {
                    discounts: (existingDiscounts = [], { readField }) => {
                        if (!existingDiscounts)
                            existingDiscounts = [];
                        let discounts = existingDiscounts.map(discount => {
                            if (readField('scope', discount) === 'EVENT') {
                                return Object.assign(Object.assign({}, discount), { guests: newGuests });
                            }
                            else {
                                return discount;
                            }
                        });
                        if (!discounts.some(discount => readField('scope', discount) === 'EVENT')) {
                            discounts = [
                                // @ts-ignore
                                ...discounts,
                                // @ts-ignore
                                {
                                    // @ts-ignore
                                    __typename: "Discount",
                                    // @ts-ignore
                                    scope: 'EVENT',
                                    guests: newGuests,
                                }
                            ];
                        }
                        // @ts-ignore
                        return discounts;
                    }
                }
            });
            // overlay: reflect updated discounts including guests
            const updated = client.cache.readFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment GetAllDiscounts on MembershipTier {
            discounts {
              scope
              percentage
              guests
            }
          }
        `
            });
            setUnsavedTierEdits(tier.id, {
                discounts: ((updated === null || updated === void 0 ? void 0 : updated.discounts) || []).map((d) => ({ scope: d.scope, percentage: d.percentage, guests: d.guests }))
            });
        };
    };
    // KGAUDIT - do we really consider scopes like this? but whatever the code works.
    const updateTierDiscount = (scope) => {
        return (ev) => {
            var _a;
            let newDiscount = stripHtml(ev.target.value).match(/^\d+$/) ? parseFloat(stripHtml(ev.target.value)) : 0;
            newDiscount = newDiscount > 100 ? 100 : newDiscount;
            client.cache.modify({
                id: `MembershipTier:${tier.id}`,
                fields: {
                    discounts: (existingDiscounts = [], { readField, toReference }) => {
                        const newDiscountObj = {
                            scope,
                            percentage: newDiscount,
                            guests: parseFloat(eventDiscountGuestsRef.current) || 0,
                        };
                        // Check if a discount with the same scope exists
                        const discountExists = existingDiscounts.some((discountRef) => readField('scope', discountRef) === scope);
                        if (!discountExists) {
                            return [...existingDiscounts, newDiscountObj];
                        }
                        // Update existing discount
                        return existingDiscounts.map((discountRef) => {
                            if (readField('scope', discountRef) === scope) {
                                return newDiscountObj;
                            }
                            return discountRef;
                        });
                    }
                }
            });
            const updatedTier = client.cache.readFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment GetUpdatedDiscounts on MembershipTier {
            discounts {
              scope
              percentage
              guests
            }
          }
        `
            });
            // overlay: reflect discounts by scope
            setUnsavedTierEdits(tier.id, {
                discounts: ((_a = updatedTier === null || updatedTier === void 0 ? void 0 : updatedTier.discounts) === null || _a === void 0 ? void 0 : _a.map((d) => ({ scope: d.scope, percentage: d.percentage, guests: d.guests }))) || []
            });
        };
    };
    const deleteTier = () => {
        // mixpanel.track(PrimaryAction, { action: MembershipLevelAction.DeletedMembershipLevel })
        setShowModal(true);
    };
    const actuallyDeleteTier = async () => {
        primaryActionLoadingVar(true);
        setShowModal(false);
        await deleteMembershipTier();
        await syncToShopify();
        primaryActionLoadingVar(false);
    };
    // KGAUDIT - not into return to safety
    const confirmDelete = (_jsx(ModalConfirmation, { message: "Are you sure you want to delete this tier?", buttonConfirm: actuallyDeleteTier, buttonConfirmContents: `Delete membership tier: ${(_u = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _u === void 0 ? void 0 : _u.name}`, buttonReject: () => setShowModal(false), buttonRejectContents: `Return to safety` }));
    var tier = data === null || data === void 0 ? void 0 : data.membershipTier;
    // Prefer overlay values when present
    const allOverlays = useReactiveVar(unsavedMembershipTierEditsVar);
    const overlay = (tier === null || tier === void 0 ? void 0 : tier.id) ? (allOverlays === null || allOverlays === void 0 ? void 0 : allOverlays[tier.id]) || {} : {};
    const overlayMonthlyPrice = (_x = (_w = (_v = overlay === null || overlay === void 0 ? void 0 : overlay.plans) === null || _v === void 0 ? void 0 : _v.find((p) => (p === null || p === void 0 ? void 0 : p.kind) === 'MONTHLY')) === null || _w === void 0 ? void 0 : _w.price) === null || _x === void 0 ? void 0 : _x.amount;
    const monthlyPrice = (overlayMonthlyPrice !== undefined && overlayMonthlyPrice !== null)
        ? overlayMonthlyPrice
        : (_0 = (_z = (_y = tier === null || tier === void 0 ? void 0 : tier.plans) === null || _y === void 0 ? void 0 : _y.find(plan => (plan.kind === 'MONTHLY'))) === null || _z === void 0 ? void 0 : _z.price) === null || _0 === void 0 ? void 0 : _0.amount;
    const overlayAnnualPrice = (_3 = (_2 = (_1 = overlay === null || overlay === void 0 ? void 0 : overlay.plans) === null || _1 === void 0 ? void 0 : _1.find((p) => (p === null || p === void 0 ? void 0 : p.kind) === 'ANNUAL')) === null || _2 === void 0 ? void 0 : _2.price) === null || _3 === void 0 ? void 0 : _3.amount;
    const annualPrice = (overlayAnnualPrice !== undefined && overlayAnnualPrice !== null)
        ? overlayAnnualPrice
        : (((_6 = (_5 = (_4 = tier === null || tier === void 0 ? void 0 : tier.plans) === null || _4 === void 0 ? void 0 : _4.find(plan => (plan.kind === 'ANNUAL'))) === null || _5 === void 0 ? void 0 : _5.price) === null || _6 === void 0 ? void 0 : _6.amount) || 0);
    const overlayEventDiscount = (_8 = (_7 = ((overlay === null || overlay === void 0 ? void 0 : overlay.discounts) || [])) === null || _7 === void 0 ? void 0 : _7.find((d) => (d === null || d === void 0 ? void 0 : d.scope) === 'EVENT')) === null || _8 === void 0 ? void 0 : _8.percentage;
    const eventDiscount = (overlayEventDiscount !== undefined && overlayEventDiscount !== null)
        ? overlayEventDiscount
        : ((_11 = (_10 = (_9 = tier === null || tier === void 0 ? void 0 : tier.discounts) === null || _9 === void 0 ? void 0 : _9.find(discount => (discount.scope === 'EVENT'))) === null || _10 === void 0 ? void 0 : _10.percentage) !== null && _11 !== void 0 ? _11 : 0);
    const overlayEventGuests = (_13 = (_12 = ((overlay === null || overlay === void 0 ? void 0 : overlay.discounts) || [])) === null || _12 === void 0 ? void 0 : _12.find((d) => (d === null || d === void 0 ? void 0 : d.scope) === 'EVENT')) === null || _13 === void 0 ? void 0 : _13.guests;
    const eventDiscountGuests = (overlayEventGuests !== undefined && overlayEventGuests !== null)
        ? overlayEventGuests
        : ((_16 = (_15 = (_14 = tier === null || tier === void 0 ? void 0 : tier.discounts) === null || _14 === void 0 ? void 0 : _14.find(discount => (discount.scope === 'EVENT'))) === null || _15 === void 0 ? void 0 : _15.guests) !== null && _16 !== void 0 ? _16 : 0);
    const overlayRetailDiscount = (_18 = (_17 = ((overlay === null || overlay === void 0 ? void 0 : overlay.discounts) || [])) === null || _17 === void 0 ? void 0 : _17.find((d) => (d === null || d === void 0 ? void 0 : d.scope) === 'RETAIL')) === null || _18 === void 0 ? void 0 : _18.percentage;
    const retailDiscount = (overlayRetailDiscount !== undefined && overlayRetailDiscount !== null)
        ? overlayRetailDiscount
        : ((_21 = (_20 = (_19 = tier === null || tier === void 0 ? void 0 : tier.discounts) === null || _19 === void 0 ? void 0 : _19.find(discount => (discount.scope === 'RETAIL'))) === null || _20 === void 0 ? void 0 : _20.percentage) !== null && _21 !== void 0 ? _21 : 0);
    // Initialize local inputs from overlay/cache when not focused
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!retailFocused)
            setRetailDiscountInput(((_a = overlayRetailDiscount !== null && overlayRetailDiscount !== void 0 ? overlayRetailDiscount : retailDiscount) !== null && _a !== void 0 ? _a : 0).toString());
        if (!eventFocused)
            setEventDiscountInput(((_b = overlayEventDiscount !== null && overlayEventDiscount !== void 0 ? overlayEventDiscount : eventDiscount) !== null && _b !== void 0 ? _b : 0).toString());
        if (!guestsFocused)
            setEventGuestsInput(((_c = overlayEventGuests !== null && overlayEventGuests !== void 0 ? overlayEventGuests : eventDiscountGuests) !== null && _c !== void 0 ? _c : 0).toString());
        if (!birthdayFocused)
            setBirthdayAmountInput(((_d = birthdayCouponAmountRef.current) === null || _d === void 0 ? void 0 : _d.toString()) || '0');
    }, [overlayRetailDiscount, retailDiscount, overlayEventDiscount, eventDiscount, overlayEventGuests, eventDiscountGuests, tier === null || tier === void 0 ? void 0 : tier.id]);
    const getEffectiveDiscounts = () => {
        if (overlay === null || overlay === void 0 ? void 0 : overlay.discounts)
            return overlay.discounts;
        return ((tier === null || tier === void 0 ? void 0 : tier.discounts) || []).map((d) => ({ scope: d === null || d === void 0 ? void 0 : d.scope, percentage: d === null || d === void 0 ? void 0 : d.percentage, guests: d === null || d === void 0 ? void 0 : d.guests }));
    };
    const writeDiscounts = (updates) => {
        const current = client.cache.readFragment({
            id: `MembershipTier:${tier.id}`,
            fragment: gql `
        fragment ReadAllDiscounts on MembershipTier {
          discounts { scope percentage guests }
        }
      `
        });
        const byScope = {};
        ((current === null || current === void 0 ? void 0 : current.discounts) || []).forEach((d) => {
            byScope[d.scope] = { scope: d.scope, percentage: d.percentage, guests: d.guests };
        });
        updates.forEach((u) => {
            byScope[u.scope] = Object.assign(Object.assign(Object.assign({}, byScope[u.scope]), u), { scope: u.scope });
        });
        const next = Object.values(byScope).map((d) => ({ __typename: 'Discount', scope: d.scope, percentage: d.percentage || 0, guests: d.guests || 0 }));
        client.writeFragment({
            id: `MembershipTier:${tier.id}`,
            fragment: gql `
        fragment WriteAllDiscounts on MembershipTier {
          discounts { __typename scope percentage guests }
        }
      `,
            data: { discounts: next }
        });
    };
    const onChangeRetailDiscount = (ev) => {
        pageHasUnsavedChangesVar(true);
        membershipsHaveBeenEditedVar(true);
        const raw = stripHtml(ev.target.value);
        const digits = raw.replace(/[^0-9]/g, '');
        const clamped = digits === '' ? '' : Math.min(100, parseInt(digits, 10)).toString();
        setRetailDiscountInput(clamped);
        const value = clamped === '' ? 0 : parseInt(clamped, 10);
        // Update overlay immediately
        const base = getEffectiveDiscounts();
        const next = base.some(d => d.scope === 'RETAIL')
            ? base.map(d => d.scope === 'RETAIL' ? Object.assign(Object.assign({}, d), { percentage: value }) : d)
            : [...base, { scope: 'RETAIL', percentage: value, guests: 0 }];
        setUnsavedTierEdits(tier.id, { discounts: next });
        // Update cache so other UI reflects immediately
        writeDiscounts([{ scope: 'RETAIL', percentage: value, guests: parseFloat(eventDiscountGuestsRef.current) || 0 }]);
    };
    const onChangeEventDiscount = (ev) => {
        pageHasUnsavedChangesVar(true);
        membershipsHaveBeenEditedVar(true);
        const raw = stripHtml(ev.target.value);
        const digits = raw.replace(/[^0-9]/g, '');
        const clamped = digits === '' ? '' : Math.min(100, parseInt(digits, 10)).toString();
        setEventDiscountInput(clamped);
        const value = clamped === '' ? 0 : parseInt(clamped, 10);
        const base = getEffectiveDiscounts();
        const guests = parseFloat(eventDiscountGuestsRef.current || eventGuestsInput || '0') || 0;
        const next = base.some(d => d.scope === 'EVENT')
            ? base.map(d => d.scope === 'EVENT' ? Object.assign(Object.assign({}, d), { percentage: value, guests }) : d)
            : [...base, { scope: 'EVENT', percentage: value, guests }];
        setUnsavedTierEdits(tier.id, { discounts: next });
        writeDiscounts([{ scope: 'EVENT', percentage: value, guests }]);
    };
    const onChangeEventGuests = (ev) => {
        pageHasUnsavedChangesVar(true);
        membershipsHaveBeenEditedVar(true);
        const raw = stripHtml(ev.target.value);
        const digits = raw.replace(/[^0-9]/g, '');
        const normalized = digits === '' ? '' : parseInt(digits, 10).toString();
        setEventGuestsInput(normalized);
        const guests = normalized === '' ? 0 : parseInt(normalized, 10);
        const base = getEffectiveDiscounts();
        const percentage = parseFloat(eventDiscountInput || '0') || 0;
        const next = base.some(d => d.scope === 'EVENT')
            ? base.map(d => d.scope === 'EVENT' ? Object.assign(Object.assign({}, d), { percentage, guests }) : d)
            : [...base, { scope: 'EVENT', percentage, guests }];
        setUnsavedTierEdits(tier.id, { discounts: next });
        writeDiscounts([{ scope: 'EVENT', percentage, guests }]);
    };
    const onChangeBirthdayAmount = (ev) => {
        pageHasUnsavedChangesVar(true);
        const raw = stripHtml(ev.target.value);
        const cleaned = raw.replace(/[^0-9.]/g, '');
        const parts = cleaned.split('.');
        const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : parts[0];
        setBirthdayAmountInput(normalized);
        const newAmount = normalized === '' ? 0 : parseFloat(normalized);
        client.writeFragment({
            id: `MembershipTier:${tier.id}`,
            fragment: gql `
        fragment UpdateBirthdayCouponAmountLocal on MembershipTier { birthdayCouponAmount }
      `,
            data: { birthdayCouponAmount: newAmount }
        });
    };
    retailDiscountRef.current = retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.toString();
    eventDiscountRef.current = eventDiscount === null || eventDiscount === void 0 ? void 0 : eventDiscount.toString();
    eventDiscountGuestsRef.current = eventDiscountGuests === null || eventDiscountGuests === void 0 ? void 0 : eventDiscountGuests.toString();
    const expandedMonthlyPrice = minorToMajorPrice(monthlyPrice);
    monthlyPriceRef.current = Number.isInteger(expandedMonthlyPrice) ? expandedMonthlyPrice.toString() : expandedMonthlyPrice.toFixed(getPrecision(currencyCodeVar()));
    shippingWeightRef.current = ((_24 = (_23 = (_22 = tier === null || tier === void 0 ? void 0 : tier.subscriptionBenefits) === null || _22 === void 0 ? void 0 : _22.find(benefit => benefit.shippingWeight)) === null || _23 === void 0 ? void 0 : _23.shippingWeight) === null || _24 === void 0 ? void 0 : _24.toString()) || "0";
    // Birthday coupon refs
    birthdayCouponAmountRef.current = ((_25 = tier === null || tier === void 0 ? void 0 : tier.birthdayCouponAmount) === null || _25 === void 0 ? void 0 : _25.toString()) || "0";
    birthdayCouponKindRef.current = (tier === null || tier === void 0 ? void 0 : tier.birthdayCouponKind) || "AMOUNT";
    const [didOnce, setDidOnce] = useState(false);
    useEffect(() => {
        var _a, _b;
        if (!didOnce && data && (strategyPrice || strategyDiscount)) {
            // Begin: Mystery chatgpt code, replaced writeMembershipTier(tier.id, {price: strategyPrice, discount: strategyDiscount}, client)
            // Only write the strategy values if they haven't been modified: 
            const currentRetailDiscount = (_b = (_a = tier === null || tier === void 0 ? void 0 : tier.discounts) === null || _a === void 0 ? void 0 : _a.find(d => d.scope === 'RETAIL')) === null || _b === void 0 ? void 0 : _b.percentage;
            const shouldWriteDiscount = currentRetailDiscount === undefined || currentRetailDiscount === 0;
            writeMembershipTier(tier.id, {
                price: strategyPrice,
                discount: shouldWriteDiscount ? strategyDiscount : currentRetailDiscount
            }, client);
            // END: Mystery chatgpt code
            setDidOnce(true);
        }
    }, [data, strategyPrice, strategyDiscount]);
    if (loading)
        return _jsx(LoadingScreen, {});
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    if (!data)
        return _jsx(ErrorScreen, { message: "Unknown error, please contact support@withfriends.co" });
    // KGAUDIT - wonder why we killed this.
    const editSubscriptionBenefit = () => {
        var _a, _b, _c;
        // mixpanel.track(PrimaryAction, { action: MembershipLevelAction.EditedSubscriptionBenefit })
        // @ts-ignore
        const id = (_c = (_b = (_a = data.membershipTier) === null || _a === void 0 ? void 0 : _a.benefits) === null || _b === void 0 ? void 0 : _b.find(isSubscriptionBenefit)) === null || _c === void 0 ? void 0 : _c.id;
        // const redirect = Redirect.create(app)
        // redirect.dispatch(Redirect.Action.APP, `/memberships/${id}`)
    };
    // KGAUDIT - this would be an inaccurate isLaunched, should have same logic as LaunchScreen I think
    const isLaunched = ((_26 = tier === null || tier === void 0 ? void 0 : tier.business) === null || _26 === void 0 ? void 0 : _26.status) === 'Live';
    // KGAUDIT - this annual price is wrong.
    // Flag for subscription-focused tiers
    const isSubscriptionTier = Boolean(tier === null || tier === void 0 ? void 0 : tier.isSubscription);
    const updateTierShippingIncluded = () => {
        return (ev) => {
            var _a;
            // mixpanel.track(PrimaryAction, { 
            //   action: MembershipLevelAction.EditedMembershipLevel,
            //   field: "shippingIncluded",
            // })
            pageHasUnsavedChangesVar(true);
            // Update all subscription benefits for this tier
            (_a = tier.subscriptionBenefits) === null || _a === void 0 ? void 0 : _a.forEach(subscriptionBenefit => {
                client.writeFragment({
                    id: `SubscriptionBenefit:${subscriptionBenefit.id}`,
                    fragment: gql `
            fragment UpdateShippingIncluded on SubscriptionBenefit {
              shippingIncluded
            }
          `,
                    data: {
                        shippingIncluded: ev.target.checked
                    }
                });
            });
        };
    };
    // Update birthday coupon amount
    const updateBirthdayCouponAmount = () => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            const newAmount = stripHtml(ev.target.value).match(/^\d+(\.\d+)?$/) ? parseFloat(stripHtml(ev.target.value)) : 0;
            client.writeFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment UpdateBirthdayCouponAmount on MembershipTier {
            birthdayCouponAmount
          }
        `,
                data: {
                    birthdayCouponAmount: newAmount
                }
            });
        };
    };
    // Update birthday coupon kind
    const updateBirthdayCouponKind = () => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            client.writeFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment UpdateBirthdayCouponKind on MembershipTier {
            birthdayCouponKind
          }
        `,
                data: {
                    birthdayCouponKind: ev.target.value
                }
            });
        };
    };
    const updateTierShippingWeight = () => {
        return (ev) => {
            var _a;
            // mixpanel.track(PrimaryAction, { 
            //   action: MembershipLevelAction.EditedMembershipLevel,
            //   field: "shippingWeight",
            // })
            pageHasUnsavedChangesVar(true);
            if (stripHtml(ev.target.value).match(/^(\d*\.)?\d+$/)) {
                let newShippingWeight = parseFloat(stripHtml(ev.target.value));
                // Update all subscription benefits for this tier
                (_a = tier.subscriptionBenefits) === null || _a === void 0 ? void 0 : _a.forEach(subscriptionBenefit => {
                    client.writeFragment({
                        id: `SubscriptionBenefit:${subscriptionBenefit.id}`,
                        fragment: gql `
              fragment UpdateShippingWeight on SubscriptionBenefit {
                shippingWeight
              }
            `,
                        data: {
                            shippingWeight: newShippingWeight
                        }
                    });
                });
            }
        };
    };
    // NEW: toggle early access RSVP setting
    const updateTierEarlyAccess = () => {
        return (ev) => {
            pageHasUnsavedChangesVar(true);
            client.writeFragment({
                id: `MembershipTier:${tier.id}`,
                fragment: gql `
          fragment UpdateEarlyAccess on MembershipTier {
            earlyAccess
          }
        `,
                data: {
                    earlyAccess: ev.target.checked,
                },
            });
            setUnsavedTierEdits(tier.id, { earlyAccess: ev.target.checked });
        };
    };
    const duplicateTier = async () => {
        var _a, _b, _c, _d, _e;
        try {
            startPrimaryAction();
            const businessId = (_b = (_a = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _a === void 0 ? void 0 : _a.business) === null || _b === void 0 ? void 0 : _b.id;
            if (!businessId) {
                completePrimaryAction();
                return;
            }
            // Quick-save only this tier, so any unsaved edits are persisted before duplication
            await saveMemberships(businessId, client, { makeNonDefault: membershipsHaveBeenEdited }, [id]);
            const source = client.readFragment({
                id: `MembershipTier:${id}`,
                fragment: DUPLICATE_TIER_SOURCE,
            });
            const nameBase = decodeSanitizedText(source === null || source === void 0 ? void 0 : source.name) || 'New Membership';
            // Phase 1: create the tier (no benefits; welcome gift can be set by id at create time)
            const createInput = Object.assign({ name: `${nameBase} (Copy)`, defaultMode: false, overridePlatformProductSync: source === null || source === void 0 ? void 0 : source.overridePlatformProductSync, plans: ((source === null || source === void 0 ? void 0 : source.plans) || []).map((p) => { var _a; return ({ kind: p === null || p === void 0 ? void 0 : p.kind, price: { amount: (_a = p === null || p === void 0 ? void 0 : p.price) === null || _a === void 0 ? void 0 : _a.amount } }); }), discounts: ((source === null || source === void 0 ? void 0 : source.discounts) || []).map((d) => ({ scope: d === null || d === void 0 ? void 0 : d.scope, percentage: d === null || d === void 0 ? void 0 : d.percentage, guests: d === null || d === void 0 ? void 0 : d.guests })), earlyAccess: source === null || source === void 0 ? void 0 : source.earlyAccess, birthdayCouponAmount: source === null || source === void 0 ? void 0 : source.birthdayCouponAmount, birthdayCouponKind: source === null || source === void 0 ? void 0 : source.birthdayCouponKind, imageId: (_c = source === null || source === void 0 ? void 0 : source.image) === null || _c === void 0 ? void 0 : _c.id }, ((tier === null || tier === void 0 ? void 0 : tier.isSubscription) ? { isSubscription: true } : {}));
            const createRes = await updateMembership({
                variables: { id: businessId, membershipInput: createInput },
                update: (cache, response) => {
                    var _a;
                    const created = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.updateOrCreateMembership;
                    if (!(created === null || created === void 0 ? void 0 : created.id))
                        return;
                    cache.modify({
                        id: `Business:${businessId}`,
                        fields: {
                            membershipTiers(existing = [], { readField }) {
                                const newRef = { __ref: `MembershipTier:${created.id}` };
                                if (existing === null || existing === void 0 ? void 0 : existing.some(ref => readField('id', ref) === created.id))
                                    return existing;
                                return [...existing, newRef];
                            }
                        }
                    });
                }
            });
            const createdTierId = (_e = (_d = createRes === null || createRes === void 0 ? void 0 : createRes.data) === null || _d === void 0 ? void 0 : _d.updateOrCreateMembership) === null || _e === void 0 ? void 0 : _e.id;
            // Phase 2: attach benefits by ID via update path
            if (createdTierId) {
                const benefitsInput = ((source === null || source === void 0 ? void 0 : source.benefits) || [])
                    // Filter out welcome gift – handled separately at top-level
                    .filter((b) => (b === null || b === void 0 ? void 0 : b.__typename) !== 'WelcomeGiftBenefit')
                    .map((b) => {
                    if ((b === null || b === void 0 ? void 0 : b.__typename) === 'SubscriptionBenefit' || 'delivery' in b) {
                        return { subscription: { id: b === null || b === void 0 ? void 0 : b.id } };
                    }
                    return { regular: { id: b === null || b === void 0 ? void 0 : b.id, order: b === null || b === void 0 ? void 0 : b.order } };
                });
                if (benefitsInput.length) {
                    await updateMembership({
                        variables: { id: businessId, membershipInput: { id: createdTierId, benefits: benefitsInput } },
                    });
                }
            }
            await syncToShopify();
            pageHasUnsavedChangesVar(false);
            clearUnsavedTierEdits();
            completePrimaryAction();
            return createRes;
        }
        catch (e) {
            console.error(e);
            completePrimaryAction();
        }
    };
    const sizeStyle = layoutVariant === 'multi'
        ? { maxWidth: 375 }
        : { width: '100%', maxWidth: 375 };
    return (_jsx(IconContext.Provider, { value: {
            color: "#8c8c8c",
            className: "absolute cursor-pointer",
            size: "24px",
            style: { top: "calc(50% - 12px)", right: ".5rem", width: "1.3em" }
        }, children: _jsxs("div", { className: `${layoutVariant === 'multi' ? 'sm:flex-330 sm:w-330 sm:flex-3column ' : ''}flex-col text-center mb-4 sm:mx-4 pb-4 border-2 border-wfGray-300 border-solid hover:border-2 hover:border-salmon-600`, style: sizeStyle, children: [showModal && confirmDelete, _jsx(MembershipLevelImageUpload, { tier: tier }), _jsxs("div", { className: "p-4 font-bold relative", children: [_jsxs("div", { className: "block mb-8", onClick: () => setEditedName(true), children: [!isLaunched && !!position && (_jsx("div", { children: _jsx(BsQuestionCircleFill, { "data-tooltip-id": "membership-level", "data-tooltip-html": helpText.membershipLevel.subscriptionBox[position], style: { top: "1em", right: "1.5em" } }) })), _jsx("span", { className: `MembershipTier-${tier.id}-Name-Finder`, children: _jsx(TextInput, { id: `tier-name-${tier.id}`, style: { background: "none" }, className: "w-full inline-block font-bold text-xl text-center underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", innerRef: tierNameInputRef, variant: TextInputVariants.Borderless, value: (_27 = overlay === null || overlay === void 0 ? void 0 : overlay.name) !== null && _27 !== void 0 ? _27 : tier.name, onChange: updateTierName(tier.id) }) }), _jsxs("label", { className: "inline-block relative overflow", htmlFor: `tier-name-${tier.id}`, children: [!editedName && (_jsx(SvgEdit24Px, { className: "text-wfGray-600 cursor-pointer absolute animate-ping fill-current", onClick: () => {
                                                var _a, _b;
                                                (_a = tierNameInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                                (_b = tierNameInputRef.current) === null || _b === void 0 ? void 0 : _b.select();
                                            } })), _jsx(SvgEdit24Px, { onClick: () => {
                                                var _a;
                                                (_a = tierNameInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                            }, className: "text-wfGray-600 cursor-pointer fill-current" })] })] }), _jsxs("div", { className: "relative flex justify-center", children: [monthlyPrice !== undefined ? (_jsx(_Fragment, { children: _jsxs("div", { className: "flex", children: [_jsx("span", { className: "self-start mt-2 font-light text-right", children: currencySymbol }), _jsx(ContentEditable, { id: `tier-price-${tier.id}`, html: monthlyPriceRef.current, className: `text-6xl font-light text-center inline underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600${(!!tier.members.length) ? ' text-wfGray-600' : ''}`, onChange: debounce((ev) => updateTierPrice(tier.id)(ev), 300), disabled: !!tier.members.length, onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: priceInputRef }), _jsx("span", { className: "self-end ml-2 font-light", children: "/mo" })] }) })) : (_jsxs("div", { children: [_jsx("span", { className: "absolute top-07 -left-07 font-light", children: currencySymbol }), _jsx("span", { className: "text-6xl font-light", children: annualPrice * 0.01 }), _jsx("span", { className: "font-light", children: "/yr" })] })), !isLaunched && !!position && (_jsx(BsQuestionCircleFill, { "data-tooltip-id": "membership-level", "data-tooltip-html": helpText.membershipLevel.price[position] }))] }), (currentShopifyVersion == 2 && ((_30 = (_29 = (_28 = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _28 === void 0 ? void 0 : _28.subscriptionBenefits) === null || _29 === void 0 ? void 0 : _29.length) !== null && _30 !== void 0 ? _30 : 0) > 0) && (_jsxs("div", { className: "mt-3 cursor-pointer", children: [_jsx("input", { className: "mr-1.5 -ml-3.5 inline align-middle cursor-pointer", type: "checkbox", name: `subscription-box-shipping-included`, id: `subscription-box-shipping-included-${tier.id}`, checked: (_32 = (_31 = data === null || data === void 0 ? void 0 : data.membershipTier) === null || _31 === void 0 ? void 0 : _31.subscriptionBenefits) === null || _32 === void 0 ? void 0 : _32.some((benefit) => benefit.shippingIncluded), onChange: updateTierShippingIncluded() }), _jsx("label", { className: "inline font-sans normal-case cursor-pointer", htmlFor: `subscription-box-shipping-included-${tier.id}`, style: { fontSize: ".8em" }, children: "shipping included" })] })), _jsx(SvgEdit24Px, { onClick: () => {
                                priceInputRef.current.focus();
                                setTimeout(() => {
                                    priceInputRef.current.focus();
                                    document.execCommand('selectAll', false, null);
                                }, 0);
                            }, className: "text-wfGray-600 cursor-pointer fill-current mx-auto mt-2" })] }), tier.members.length ? (_jsx("div", { className: "text-wfGray-600 text-xs", style: currentShopifyVersion != 2 ? {
                        margin: "auto",
                        maxWidth: "280px"
                    } : null, children: "Once a tier has members, you can't change the price. Visit Advanced Options for more options." })) : null, (currentShopifyVersion == 2 && ((_33 = data.membershipTier) === null || _33 === void 0 ? void 0 : _33.subscriptionBenefits.length) > 0 && data.membershipTier.subscriptionBenefits.some((benefit) => !benefit.shippingIncluded)) && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mt-4 px-4 text-center", children: ["Charge shipping for", _jsx("br", {}), "a", _jsx(ContentEditable, { html: shippingWeightRef.current, id: `tier-shipping-weight-${tier.id}`, className: "inline underline mx-1 focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", onChange: debounce((ev) => updateTierShippingWeight()(ev), 300), onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: shippingWeightInputRef }), "lb. box"] }), _jsx(SvgEdit24Px, { onClick: () => {
                                shippingWeightInputRef.current.focus();
                                setTimeout(() => {
                                    shippingWeightInputRef.current.focus();
                                    document.execCommand('selectAll', false, null);
                                }, 0);
                            }, className: "text-wfGray-600 cursor-pointer fill-current mx-auto mb-4" })] })), _jsxs("div", { className: "mt-4 px-4 text-center", children: [
                        //@ts-ignore
                        _jsx(BenefitList, { kind: "subscription", tier: tier, position: !isLaunched ? position : null }), 
                        //@ts-ignore
                        _jsx(BenefitList, { kind: "welcome", tier: tier, position: !isLaunched ? position : null }), 
                        //@ts-ignore
                        _jsx(BenefitList, { kind: "redemption", tier: tier, position: !isLaunched ? position : null }), !tier.isSubscription && (_jsxs(_Fragment, { children: [_jsxs("div", { className: `text-center mb-1 leading-6 relative ${retailDiscountInput === '0' ? 'text-wfGray-400' : ''}`, children: [_jsx(ContentEditable, { html: retailDiscountInput, id: `tier-discount-retail-${tier.id}`, className: "inline underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", onChange: onChangeRetailDiscount, onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: retailDiscountInputRef, onFocus: () => setRetailFocused(true), onBlur: () => setRetailFocused(false) }), "% discount", _jsx("br", {}), "on all store purchases", !isLaunched && !!position && (_jsx(BsQuestionCircleFill, { "data-tooltip-id": "membership-level", "data-tooltip-html": helpText.membershipLevel.discount }))] }), _jsx(SvgEdit24Px, { onClick: () => {
                                        retailDiscountInputRef.current.focus();
                                        setTimeout(() => {
                                            retailDiscountInputRef.current.focus();
                                            document.execCommand('selectAll', false, null);
                                        }, 0);
                                    }, className: "text-wfGray-600 cursor-pointer fill-current mx-auto mb-4" }), _jsxs("div", { className: `text-center mb-4 leading-6 relative ${birthdayAmountInput === '0' || birthdayAmountInput === '' ? 'text-wfGray-400' : ''}`, children: [birthdayCouponKindRef.current === 'AMOUNT' && (_jsx("span", { className: "mr-1", children: currencySymbol })), _jsx(ContentEditable, { html: birthdayAmountInput, id: `tier-birthday-coupon-amount-${tier.id}`, className: "inline underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", onChange: onChangeBirthdayAmount, onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: birthdayCouponAmountInputRef, onFocus: () => setBirthdayFocused(true), onBlur: () => setBirthdayFocused(false) }), _jsxs("select", { value: birthdayCouponKindRef.current, onChange: updateBirthdayCouponKind(), className: "inline ml-1 border border-wfGray-300 rounded text-sm focus:outline-none cursor-pointer", children: [_jsx("option", { value: "PERCENT", children: "% off one order" }), _jsx("option", { value: "AMOUNT", children: "store credit" })] }), _jsx("span", { className: "ml-1", children: "birthday coupon" })] }), _jsx(SvgEdit24Px, { onClick: () => {
                                        birthdayCouponAmountInputRef.current.focus();
                                        setTimeout(() => {
                                            birthdayCouponAmountInputRef.current.focus();
                                            document.execCommand('selectAll', false, null);
                                        }, 0);
                                    }, className: "text-wfGray-600 cursor-pointer fill-current mx-auto mb-4" }), _jsxs("div", { className: `text-center mb-1 leading-6 relative ${eventDiscountInput === '0' ? 'text-wfGray-400' : ''}`, children: [_jsx(ContentEditable, { html: eventDiscountInput, id: `tier-discount-event-${tier.id}`, className: "inline underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600", onChange: onChangeEventDiscount, onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: eventDiscountInputRef, onFocus: () => setEventFocused(true), onBlur: () => setEventFocused(false) }), "% discount", _jsx("br", {}), eventDiscountRef.current === '100' ? '(Free entry to all events)' : 'on all online ticket purchases'] }), _jsx(SvgEdit24Px, { onClick: () => {
                                        eventDiscountInputRef.current.focus();
                                        setTimeout(() => {
                                            eventDiscountInputRef.current.focus();
                                            document.execCommand('selectAll', false, null);
                                        }, 0);
                                    }, className: "text-wfGray-600 cursor-pointer fill-current mx-auto mb-4" }), _jsxs("div", { className: `text-center mb-1 leading-6 relative ${eventDiscountInput === '0' ? 'text-wfGray-400 pointer-events-none' : (eventGuestsInput === '0' ? 'text-wfGray-400' : '')}`, children: ["Share your discount with", ' ', _jsx(ContentEditable, { html: eventGuestsInput, id: `tier-guests-${tier.id}`, className: `inline underline focus:ring-transparent focus:outline-none focus:decoration-salmon-600 focus-within:decoration-salmon-600 selection:underline selection:bg-selectionBlue selection:decoration-salmon-600`, onChange: onChangeEventGuests, onKeyPress: preventEnter, onKeyDown: handleNumberFieldKeyDown, innerRef: eventDiscountGuestsInputRef, disabled: !eventDiscount, onFocus: () => setGuestsFocused(true), onBlur: () => setGuestsFocused(false) }), ' ', "additional guest", eventDiscountGuests !== 1 ? 's' : '', " per event"] }), _jsx(SvgEdit24Px, { onClick: () => {
                                        eventDiscountGuestsInputRef.current.focus();
                                        setTimeout(() => {
                                            eventDiscountGuestsInputRef.current.focus();
                                            document.execCommand('selectAll', false, null);
                                        }, 0);
                                    }, className: `text-wfGray-600 fill-current mx-auto mb-4 ${eventDiscountRef.current === '0' ? 'pointer-events-none opacity-50' : 'cursor-pointer'}` }), _jsxs("div", { className: "text-center mb-4 leading-6 relative cursor-pointer", children: [_jsx("input", { className: "mr-1.5 -ml-3.5 inline align-middle cursor-pointer", type: "checkbox", name: `early-access`, id: `early-access-${tier.id}`, checked: (_34 = overlay === null || overlay === void 0 ? void 0 : overlay.earlyAccess) !== null && _34 !== void 0 ? _34 : !!(tier === null || tier === void 0 ? void 0 : tier.earlyAccess), onChange: updateTierEarlyAccess() }), _jsx("label", { className: "inline font-sans normal-case cursor-pointer", htmlFor: `early-access-${tier.id}`, style: { fontWeight: "initial", letterSpacing: "initial" }, children: "Early access to RSVP for special events" })] })] })), 
                        //@ts-ignore
                        _jsx(BenefitList, { kind: "regular", tier: tier, position: !isLaunched ? position : null }), !((_35 = tier.subscriptionBenefits) === null || _35 === void 0 ? void 0 : _35.some((benefit) => benefit.id)) && (_jsx(Button, { disabled: loadingCreateSubscriptionBenefit, className: "my-2", variant: ButtonVariant.Outlined, onClick: () => {
                                setLoadingCreateSubscriptionBenefit(true);
                                return createSubscriptionBenefit();
                            }, toolTip: !isLaunched ? helpText.membershipLevel.subscriptionBox[position] : null, toolTipId: 'membership-level', children: loadingCreateSubscriptionBenefit ? "Creating Box..." :
                                _jsxs(_Fragment, { children: [_jsx(SvgAddPlus24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2 -ml-1" }), _jsx("span", { className: "align-middle", children: "Add Subscription Box" })] }) })), (!((_36 = tier.welcomeGift) === null || _36 === void 0 ? void 0 : _36.id)) && (_jsx(Button, { disabled: loadingCreateWelcomeGift, className: "my-2", variant: ButtonVariant.Outlined, onClick: () => {
                                setLoadingCreateWelcomeGift(true);
                                return createWelcomeGift();
                            }, toolTip: !isLaunched ? helpText.membershipLevel.welcomeGift : null, toolTipId: 'membership-level', children: loadingCreateWelcomeGift ? "Creating Gift..." :
                                _jsxs(_Fragment, { children: [_jsx(SvgAddPlus24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2 -ml-1" }), _jsx("span", { className: "align-middle", children: "Add One-Time Welcome Gift" })] }) })), !tier.isSubscription && (_jsx(Button, { disabled: loadingCreateRedemptionBenefit, className: "my-2", variant: ButtonVariant.Outlined, onClick: () => {
                                setLoadingCreateRedemptionBenefit(true);
                                // @ts-ignore
                                return createRedemptionBenefit();
                            }, children: loadingCreateRedemptionBenefit ? "Creating Redemption..." :
                                _jsxs(_Fragment, { children: [_jsx(SvgAddPlus24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2 -ml-1" }), _jsx("span", { className: "align-middle", children: "Add Redemption Benefit" })] }) })), _jsx(Button, { className: "my-2", variant: ButtonVariant.Outlined, onClick: async () => {
                                startPrimaryAction();
                                setLoadingCreateBenefit(true);
                                await saveMemberships(currentBusinessId, client, { makeNonDefault: membershipsHaveBeenEdited });
                                await syncToShopify();
                                pageHasUnsavedChangesVar(false);
                                clearUnsavedTierEdits();
                                await createBenefit();
                                completePrimaryAction();
                            }, toolTip: !isLaunched ? helpText.membershipLevel.writeInBenefit : null, toolTipId: 'membership-level', children: loadingCreateBenefit ? "Creating Benefit..." :
                                _jsxs(_Fragment, { children: [_jsx(SvgAddPlus24Px, { className: "text-wfGray-600 inline cursor-pointer align-middle fill-current mr-2 -ml-1" }), _jsx("span", { className: "align-middle", children: "Add Write-in Benefit" })] }) }), extraButtons ? extraButtons(id) : null, _jsx(Button, { className: "my-2", variant: ButtonVariant.Outlined, onClick: duplicateTier, disabled: primaryActionLoading, children: "Duplicate Tier" }), ( /*currentShopifyVersion == 2 &&*/!suppressDelete) ? (_jsxs(Button, { className: "my-2 relative", variant: ButtonVariant.Outlined, onClick: deleteTier, disabled: !!tier.members.length || primaryActionLoading, disabledError: !!tier.members.length ? "Once a tier has members, you can't delete it here. Visit Advanced Options for more options." : null, children: ["Delete Tier", _jsx(SvgX, { className: "text-wfGray-600 cursor-pointer fill-current absolute", style: { top: "calc(50% - 12px)", right: "0.375rem", transform: "scale(.7)" } })] })) : null] }), _jsx(ReactTooltip, { id: "membership-level" })] }) }));
};
export default MembershipLevel;
